import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  DecoratedCompanyFile,
  UndecoratedCompanyFile,
} from '@flemingmetrics/api-client';
import { filterByValue, mergeWithoutDuplicates } from '@/utils/arrays';
import { buildCompositeKey, Identifier } from '@/store/utils';
import { deleteCompany } from './companiesSlice';
import { deleteFile, deleteFiles } from './filesSlice';

type CompanyFilesState = {
  byId: Record<string, UndecoratedCompanyFile>;
  byCompanyId: Record<string, string[]>;
  byFileId: Record<string, string>;
};

const initialState: CompanyFilesState = {
  byId: {},
  byCompanyId: {},
  byFileId: {},
};

export const companyFilesSlice = createSlice({
  name: 'companyFiles',
  initialState,
  reducers: {
    addCompanyFile: (state, action: PayloadAction<DecoratedCompanyFile>) => {
      return getNextState(state, [action.payload]);
    },
    setCompanyFiles: (state, action: PayloadAction<DecoratedCompanyFile[]>) => {
      return getNextState(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteCompany, (state, action) => {
        const { companyId } = action.payload;
        const keys = state.byCompanyId[companyId];
        if (!keys) return state;
        const byId = { ...state.byId };
        const byCompanyId = { ...state.byCompanyId };
        const byFileId = { ...state.byFileId };
        keys.forEach((key) => {
          if (!byId[key]) return;
          delete byFileId[byId[key].fileId];
          delete byId[key];
        });
        delete byCompanyId[companyId];
        return { byId, byCompanyId, byFileId };
      })
      .addCase(deleteFile, (state, action) => {
        const { fileId } = action.payload;
        let byId = { ...state.byId };
        let byCompanyId = { ...state.byCompanyId };
        let byFileId = { ...state.byFileId };
        const key = byFileId[fileId];
        const companyFile = byId[key];
        if (!companyFile) return state;
        if (byCompanyId[companyFile.companyId]) {
          byCompanyId[companyFile.companyId] = filterByValue(
            byCompanyId[companyFile.companyId],
            key
          );
        }
        delete byFileId[fileId];
        delete byId[key];
        return { byId, byCompanyId, byFileId };
      })
      .addCase(deleteFiles, (state, action) => {
        let byId = { ...state.byId };
        let byCompanyId = { ...state.byCompanyId };
        let byFileId = { ...state.byFileId };
        action.payload.fileIds.forEach((fileId) => {
          const key = byFileId[fileId];
          const companyFile = byId[key];
          if (!companyFile) return state;
          if (byCompanyId[companyFile.companyId]) {
            byCompanyId[companyFile.companyId] = filterByValue(
              byCompanyId[companyFile.companyId],
              key
            );
          }
          delete byFileId[fileId];
          delete byId[key];
        });
        return { byId, byCompanyId, byFileId };
      });
  },
});

function formatCompanyFile({
  file,
  ...companyFile
}: DecoratedCompanyFile): UndecoratedCompanyFile {
  return companyFile;
}

function getNextState(
  state: CompanyFilesState,
  companyFiles: DecoratedCompanyFile[]
) {
  const byId = { ...state.byId };
  const byCompanyId = { ...state.byCompanyId };
  const byFileId = { ...state.byFileId };
  companyFiles.forEach((cf) => {
    const { companyId, fileId } = cf;
    const key = buildCompanyFileKey(companyId, fileId);
    byId[key] = formatCompanyFile(cf);
    if (!byCompanyId[companyId]) byCompanyId[companyId] = [];
    byCompanyId[companyId] = mergeWithoutDuplicates(byCompanyId[companyId], [
      key,
    ]);
    byFileId[fileId] = key;
  });
  return { byId, byCompanyId, byFileId };
}

export function buildCompanyFileKey(companyId: Identifier, fileId: Identifier) {
  return buildCompositeKey(companyId, fileId);
}

export const { addCompanyFile, setCompanyFiles } = companyFilesSlice.actions;

export default companyFilesSlice.reducer;
